import { DownArrowIcon } from "@xola/icons";
import { PopoverList } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useMemo, useState } from "react";

interface Option<Choice = string> {
    value: Choice;
    label: string;
}

interface PopoverListChoiceProps<Choice = string> {
    values: Array<Option<Choice>> | Choice[];
    value: Choice;
    onChange: (newValue: Choice) => void;
    disabled?: boolean;
    className?: string;
    dataTestId?: string;
}

export const PopoverListChoice = <Choice extends string = string>({
    value,
    values,
    disabled,
    onChange,
    className,
    dataTestId,
}: PopoverListChoiceProps<Choice>) => {
    const [visible, setVisible] = useState(false);

    const options: Array<Option<Choice>> = useMemo(() => {
        return values.map((item) => {
            if (typeof item === "string") {
                return {
                    value: item,
                    label: item,
                };
            }

            return item;
        });
    }, [values]);

    const displayText = options.find((option) => option.value === value)?.label;

    const toggleVisible = () => {
        setVisible(!visible);
    };

    const handleItemClick = (e: any, newValue: Choice) => {
        onChange(newValue);
        toggleVisible();
    };

    return (
        <PopoverList
            visible={visible}
            className={className}
            data-testid={dataTestId}
            onClickOutside={() => setVisible(false)}
        >
            <span
                className={clsx("inline-flex cursor-pointer items-center", {
                    "pointer-events-none !cursor-not-allowed": disabled,
                })}
                onClick={toggleVisible}
            >
                <span className="font-bold underline" data-testid={dataTestId ? `${dataTestId}-title` : undefined}>
                    {displayText}
                </span>
                <span>
                    <DownArrowIcon size="medium" />
                </span>
            </span>
            {options.map((item) => (
                <PopoverList.Item
                    key={item.value}
                    isDisabled
                    disabled
                    name={item.value}
                    isActive={value === item.value}
                    onClickItem={handleItemClick}
                >
                    <span>{item.label}</span>
                </PopoverList.Item>
            ))}
        </PopoverList>
    );
};
